import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { bulkExcelEditContents } from '../services/productsService';
import {
  EDIT_TEMPLATES,
  validateAndParseEditWorkbook,
} from '../utils/icerikExcelEditTemplates';
import './IcerikExcelEditModal.css';

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   contentType: 'urun'|'parca'|'islem',
 *   scope: { type: 'kategori', kategoriId: string, label?: string } | { type: 'urun', urunIcerikId: string, label?: string },
 *   onApplied: () => void,
 * }} props
 */
const IcerikExcelEditModal = ({ isOpen, onClose, contentType, scope, onApplied }) => {
  const fileInputRef = useRef(null);
  const [fileLabel, setFileLabel] = useState('');
  const [parseError, setParseError] = useState('');
  const [parsedPayload, setParsedPayload] = useState(null);
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const reset = () => {
    setFileLabel('');
    setParseError('');
    setParsedPayload(null);
    setLastResult(null);
  };

  const handleClose = () => {
    if (applying) return;
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setFileLabel(file.name);
    setParseError('');
    setParsedPayload(null);
    setLastResult(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheetName = wb.SheetNames && wb.SheetNames[0];
        if (!sheetName) {
          setParseError('Excel dosyasında sayfa bulunamadı.');
          return;
        }
        const ws = wb.Sheets[sheetName];
        const allRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
        const expectedScope =
          scope.type === 'kategori'
            ? { type: 'kategori', kategoriId: scope.kategoriId }
            : { type: 'urun', urunIcerikId: scope.urunIcerikId };

        const validated = validateAndParseEditWorkbook(contentType, expectedScope, allRows);
        if (!validated.ok) {
          if (validated.parseErrors?.length) {
            setParseError(validated.error);
            setLastResult({
              success: 0,
              errors: validated.parseErrors.map((er) => ({
                row: er.row,
                message: er.message,
              })),
              total: 0,
            });
          } else {
            setParseError(validated.error);
          }
          return;
        }

        setParsedPayload({
          icerikTipi: validated.icerikTipi,
          scope: validated.scope,
          templateVersion: validated.templateVersion,
          rows: validated.rows,
        });
      } catch (err) {
        setParseError(err?.message || 'Excel dosyası okunamadı');
      }
    };
    reader.onerror = () => setParseError('Dosya okunamadı');
    reader.readAsArrayBuffer(file);
  };

  const handleApply = async () => {
    if (!parsedPayload?.rows?.length) {
      setParseError('Önce geçerli bir düzenleme dosyası yükleyin.');
      return;
    }

    setApplying(true);
    setParseError('');
    setLastResult(null);

    try {
      const res = await bulkExcelEditContents(parsedPayload);
      const updated = res?.veri?.updated ?? 0;
      const apiErrors = res?.veri?.errors || [];
      setLastResult({
        success: updated,
        errors: apiErrors.map((e) => ({
          row: e.row,
          message: e._id ? `${e.message} (_id: ${e._id})` : e.message,
        })),
        total: parsedPayload.rows.length,
      });
      if (updated > 0) {
        if (window.showNotification) {
          window.showNotification('success', res?.mesaj || `${updated} kayıt güncellendi`);
        }
        if (onApplied) onApplied();
      } else if (window.showNotification) {
        window.showNotification('error', res?.mesaj || 'Güncelleme yapılamadı');
      }
    } catch (err) {
      const apiErrors = err?.data?.veri?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length) {
        setLastResult({
          success: err?.data?.veri?.updated ?? 0,
          errors: apiErrors.map((e) => ({
            row: e.row,
            message: e.message,
          })),
          total: parsedPayload.rows.length,
        });
      }
      setParseError(err?.message || 'Güncelleme isteği başarısız');
      if (window.showNotification) {
        window.showNotification('error', err?.message || 'Güncelleme başarısız');
      }
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;

  const tipTitle =
    contentType === 'urun' ? 'Ürün' : contentType === 'parca' ? 'Parça' : 'İşlem';
  const headers = EDIT_TEMPLATES[contentType]?.headers || [];
  const scopeLabel =
    scope.label ||
    (scope.type === 'kategori' ? `Kategori ${scope.kategoriId}` : `Ürün ${scope.urunIcerikId}`);

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="icerik-excel-edit-title"
      onClick={handleClose}
    >
      <div
        className="modal-content content-item-form-modal icerik-excel-edit-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="icerik-excel-edit-title">Excel ile {tipTitle} düzenle</h2>
          <p className="icerik-excel-import__subtitle">{scopeLabel}</p>
          <button type="button" className="modal-close-btn" onClick={handleClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="content-item-form-modal__scroll icerik-excel-edit__body">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="icerik-excel-edit__file-input"
            onChange={handleFileChange}
          />

          <p className="icerik-excel-edit__hint">
            Yalnızca bu ekrandan <strong>Excel ile dışarı aktar</strong> ile indirdiğiniz dosyayı kullanın.
            Başlık veya meta satırını değiştirmeyin. Sütunlar: {headers.join(', ')}.
          </p>

          <button
            type="button"
            className="secondary-btn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={applying}
          >
            <i className="fas fa-file-upload" /> Düzenlenmiş Excel seç
          </button>
          {fileLabel ? (
            <span className="icerik-excel-import__file-name">{fileLabel}</span>
          ) : null}

          {parseError ? (
            <div className="error-message" role="alert" style={{ marginTop: 12 }}>
              {parseError}
            </div>
          ) : null}

          {parsedPayload ? (
            <p className="icerik-excel-edit__row-count" role="status">
              <strong>{parsedPayload.rows.length}</strong> satır güncellenecek.
            </p>
          ) : null}

          {lastResult ? (
            <div className="icerik-excel-edit__result" role="status">
              <p>
                <strong>{lastResult.success}</strong> kayıt güncellendi
                {lastResult.total > 0 ? (
                  <>
                    , <strong>{lastResult.errors.length}</strong> hata (toplam {lastResult.total} satır)
                  </>
                ) : null}
              </p>
              {lastResult.errors.length > 0 ? (
                <ul className="icerik-excel-edit__error-list">
                  {lastResult.errors.slice(0, 40).map((e, idx) => (
                    <li key={`${e.row}-${idx}`}>
                      {e.row ? `Satır ${e.row}: ` : ''}
                      {e.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

        </div>

        <div className="modal-footer icerik-excel-import__footer">
          <div className="icerik-excel-import__footer-actions" style={{ marginLeft: 'auto' }}>
            <button type="button" className="secondary-btn" onClick={handleClose} disabled={applying}>
              Kapat
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={handleApply}
              disabled={applying || !parsedPayload}
            >
              {applying ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Uygulanıyor…
                </>
              ) : (
                'Değişiklikleri uygula'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IcerikExcelEditModal;
